import { nextTick, ref, h } from 'vue';
import { renderToString } from 'vue/server-renderer'
import { createTooltip, destroyTooltip } from 'floating-vue';
import { makeDestructurable } from '@vueuse/shared';
import { unrefElement } from '@vueuse/core';
import { deferredPromise } from '/app/utils/promise';
import { prefersReducedMotion } from '/app/utils/motion';

/**
 * Animation budget. A plain bubble lives EMERGE + duration + EXIT; a flying one
 * lives EMERGE + duration + MORPH + FLIGHT. `flash()` resolves only once the
 * bubble is fully gone, because the topic-creation flow awaits it before
 * navigating away.
 */
const EMERGE_MS = 400;
const EXIT_MS = 300;
const MORPH_MS = 200;
const FLIGHT_MS = 600;
const FADE_MS = 150;

// Diameter of the dot the pill collapses into before flying off.
const DOT_SIZE = 12;

/**
 * Monotonic id shared by every bubble in the page. Each bubble gets its own
 * `popperClass` from it, so hiding/cleanup can target its own popper instead
 * of whichever `.v-popper__popper` happens to be first in the DOM.
 */
let bubbleSeq = 0;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Finds the element a bubble should fly to.
 *
 * Both bell activators in the navbar carry the same `data-fly-target`, one per
 * breakpoint, so pick whichever is actually on screen. When none is - below
 * `lg` the navbar menu is collapsed, and the header scrolls away on long pages -
 * fall back to the top-right corner of the viewport so the dot still reads as
 * "went up there".
 *
 * @param {string} name - Value of the data-fly-target attribute
 * @returns {{x: number, y: number, anchored: boolean}} - Where to land, and
 *          whether that spot is a real element or the fallback corner
 */
const resolveFlyTarget = (name) => {
    const nodes = Array.from(document.querySelectorAll(`[data-fly-target="${name}"]`));

    const onScreen = nodes
        .map((el) => el.getBoundingClientRect())
        .filter((rect) => rect.width > 0 && rect.height > 0)
        .find((rect) => {
            const x = rect.left + rect.width / 2;
            const y = rect.top + rect.height / 2;

            return x > 0 && x < window.innerWidth && y > 0 && y < window.innerHeight;
        });

    if (onScreen) {
        return {
            x: onScreen.left + onScreen.width / 2,
            y: onScreen.top + onScreen.height / 2,
            anchored: true,
        };
    }

    return { x: window.innerWidth - 24, y: 24, anchored: false };
};

/**
 * Collapses the pill into a dot and throws it at the target.
 *
 * The arc comes from splitting the movement across the two elements we already
 * have: the wrapper carries X at a constant speed while the pill carries Y with
 * an ease-out. Their sum is a parabola, no motion path needed.
 *
 * @param {HTMLElement} popper - This bubble's `.v-popper__popper` root
 * @param {{x: number, y: number}} target - Viewport coordinates to land on
 * @returns {Promise<void>}
 */
const flyToTarget = async (popper, target) => {
    const wrapper = popper.querySelector('[data-bubble-wrapper]');
    const pill = popper.querySelector('[data-bubble-pill]');
    const label = popper.querySelector('[data-bubble-label]');

    if (!wrapper || !pill) {
        return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();

    // Take the pill out of flow at its current spot. Shrinking it in flow would
    // reflow the popper box underneath it and drag the start of the flight.
    wrapper.style.position = 'relative';
    wrapper.style.width = `${wrapperRect.width}px`;
    wrapper.style.height = `${wrapperRect.height}px`;

    pill.style.position = 'absolute';
    pill.style.left = `${pillRect.left - wrapperRect.left}px`;
    pill.style.top = `${pillRect.top - wrapperRect.top}px`;
    pill.style.width = `${pillRect.width}px`;
    pill.style.height = `${pillRect.height}px`;
    pill.style.margin = '0';

    // The emerge animation held the pill at scale(1); dropping it falls back to
    // the `scale-0 opacity-0` utilities underneath, so pin both inline.
    pill.classList.remove('animate-bubble-emerge');
    pill.style.transform = 'none';
    pill.style.opacity = '1';

    // Width and height shrink from the top-left corner, so translate the pill
    // back by half the loss to keep its centre - and the flight origin - still.
    const recenterX = (pillRect.width - DOT_SIZE) / 2;
    const recenterY = (pillRect.height - DOT_SIZE) / 2;

    const dx = target.x - (pillRect.left + pillRect.width / 2);
    const dy = target.y - (pillRect.top + pillRect.height / 2);

    // Flush the frozen geometry so the transitions below have a start value.
    void pill.offsetWidth;

    pill.style.transition = [
        `width ${MORPH_MS}ms ease`,
        `height ${MORPH_MS}ms ease`,
        `padding ${MORPH_MS}ms ease`,
        `border-radius ${MORPH_MS}ms ease`,
        `transform ${MORPH_MS}ms ease`,
    ].join(', ');
    pill.style.width = `${DOT_SIZE}px`;
    pill.style.height = `${DOT_SIZE}px`;
    pill.style.padding = '0';
    pill.style.borderRadius = '9999px';
    pill.style.transform = `translate(${recenterX}px, ${recenterY}px)`;

    if (label) {
        label.style.transition = `opacity ${MORPH_MS}ms ease`;
        label.style.opacity = '0';
    }

    await wait(MORPH_MS);

    wrapper.style.transition = `transform ${FLIGHT_MS}ms linear`;
    wrapper.style.transform = `translateX(${dx}px)`;

    const flightTransitions = [`transform ${FLIGHT_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`];

    // Landing on a real anchor, the dot is swapped for the bell badge - same
    // 12px, same colour - the frame it is destroyed, so fading it out first
    // would open a gap between the two. Only the viewport-corner fallback needs
    // the fade, because nothing takes the dot's place there.
    if (!target.anchored) {
        flightTransitions.push(
            `opacity ${Math.round(FLIGHT_MS * 0.3)}ms ease-in ${Math.round(FLIGHT_MS * 0.7)}ms`
        );
    }

    pill.style.transition = flightTransitions.join(', ');
    pill.style.transform = `translate(${recenterX}px, ${recenterY + dy}px)`;

    if (!target.anchored) {
        pill.style.opacity = '0';
    }

    await wait(FLIGHT_MS);
};

/**
 * Plays the scale-down exit animation.
 *
 * @param {HTMLElement} popper - This bubble's `.v-popper__popper` root
 * @returns {Promise<void>}
 */
const exitBubble = async (popper) => {
    const pill = popper.querySelector('[data-bubble-pill]');

    if (!pill) {
        return;
    }

    pill.classList.remove('animate-bubble-emerge');
    pill.classList.add('animate-bubble-exit');

    await wait(EXIT_MS);
};

/**
 * Fades the bubble out - the whole outro when the user asked for less motion.
 *
 * @param {HTMLElement} popper - This bubble's `.v-popper__popper` root
 * @returns {Promise<void>}
 */
const fadeBubble = async (popper) => {
    const pill = popper.querySelector('[data-bubble-pill]');

    if (!pill) {
        return;
    }

    pill.classList.remove('animate-bubble-fade');
    pill.style.transition = `opacity ${FADE_MS}ms ease`;
    pill.style.opacity = '0';

    await wait(FADE_MS);
};

export const useBubble = () => {
    // Map to control active tooltips
    const activeTooltips = new Map();

    // Per-target creation queue: two flashes on the same element run one after
    // the other, while different elements stay concurrent.
    const pendingByElement = new WeakMap();

    /**
     * Resolves a target into a usable DOM element
     * @param {string|HTMLElement|Ref} target - CSS selector, DOM element or Vue ref
     * @returns {HTMLElement|null}
     */
    const resolveElement = (target) => {
        const element = typeof target === 'string'
            ? document.querySelector(target)
            : unrefElement(target);

        if (!element || element.nodeType !== Node.ELEMENT_NODE) {
            console.warn(`Invalid element for bubble: ${target}`, element);
            return null;
        }

        return element;
    };

    /**
     * Shows a text tooltip on an element
     * @param {string|HTMLElement} target - CSS selector or DOM element
     * @param {string|VNode|Function} text - Text string, Vue component/VNode created with h(), or component function () => h()
     * @param {object} options - Tooltip options
     * @returns {Promise<string|null>} - Tooltip key, or null when it could not be created
     */
    const showBubble = async (target, text, options = {}) => {
        await nextTick();

        const element = resolveElement(target);

        if (!element) {
            return null;
        }

        // Queue behind whatever is already being created for this element
        // instead of dropping the call.
        const previous = pendingByElement.get(element) ?? Promise.resolve();
        const current = previous.then(() => createBubbleFor(element, text, options));

        pendingByElement.set(element, current.catch(() => {}));

        return current;
    };

    /**
     * Creates the tooltip for an already resolved element
     * @param {HTMLElement} element - Target element
     * @param {string|VNode|Function} text - Bubble content
     * @param {object} options - Tooltip options
     * @returns {Promise<string|null>} - Tooltip key
     */
    const createBubbleFor = async (element, text, options) => {
        try {
            // Replace any bubble still attached to this element. It has to be
            // destroyed before creating the next one, otherwise both share the
            // same `element.$_popper` and the old teardown kills the new bubble.
            const existingKey = Array.from(activeTooltips.keys()).find((key) => {
                return activeTooltips.get(key)?.element === element;
            });

            if (existingKey) {
                await hideBubble(existingKey, { immediate: true });
            }

            // Default settings
            const defaultOptions = {
                duration: 3000, // time the bubble stays up once it has emerged
                placement: 'top',
                showPlus: true,
                animated: true,
                color: 'nerve',
                flyTo: null, // data-fly-target name to throw the bubble at
                onHidden: null,
                onShown: null,
            };

            const config = {
                ...defaultOptions,
                ...options,
                reducedMotion: prefersReducedMotion(),
            };

            // Generate tooltip content (always returns HTML string now)
            const content = await generateContent(text, config);

            const tooltipKey = `bubble-${++bubbleSeq}`;

            // Tooltip configuration
            const tooltipConfig = {
                theme: 'bubble',
                html: true, // Always HTML since we render VNodes to strings
                content: content,
                placement: config.placement,
                distance: 10,
                triggers: [],
                autoHide: false,
                shown: true,
                handleResize: true,
                // Lands on the `.v-popper__popper` root and identifies this bubble.
                popperClass: tooltipKey,
            };

            // Create the tooltip
            const tooltip = createTooltip(element, tooltipConfig);

            // Store the active tooltip
            activeTooltips.set(tooltipKey, {
                tooltip,
                element,
                config,
                timeoutId: null,
            });

            // Call onShown callback if provided
            if (config.onShown) {
                config.onShown();
            }

            // Auto-hide if duration is specified
            if (config.duration > 0) {
                const timeoutId = setTimeout(() => {
                    hideBubble(tooltipKey);
                }, EMERGE_MS + config.duration);

                // Update the tooltip data with the timeout ID
                const tooltipData = activeTooltips.get(tooltipKey);
                if (tooltipData) {
                    tooltipData.timeoutId = timeoutId;
                }
            }

            return tooltipKey;
        } catch (error) {
            console.error('Error creating bubble:', error);
            return null;
        }
    };

    /**
     * Picks how the bubble leaves the screen
     * @param {HTMLElement} popper - This bubble's `.v-popper__popper` root
     * @param {object} config - Bubble configuration
     * @returns {Promise<void>}
     */
    const runOutro = (popper, config) => {
        if (config.reducedMotion) {
            return fadeBubble(popper);
        }

        if (config.flyTo) {
            return flyToTarget(popper, resolveFlyTarget(config.flyTo));
        }

        return exitBubble(popper);
    };

    /**
     * Hides a specific tooltip, playing its outro first.
     *
     * Always fires the bubble's own `onHidden`, including when replaced early -
     * a caller awaiting `flash()` would otherwise wait forever.
     *
     * @param {string} tooltipKey - Tooltip key
     * @param {object} options - `immediate: true` skips the outro
     * @returns {Promise<boolean>} - Resolves once the tooltip is destroyed
     */
    const hideBubble = (tooltipKey, { immediate = false } = {}) => {
        const tooltipData = activeTooltips.get(tooltipKey);

        if (!tooltipData) {
            return Promise.resolve(false);
        }

        const { element, config, timeoutId } = tooltipData;

        // Drop it upfront so a concurrent hide (auto-hide timeout vs. replace)
        // cannot tear down the same tooltip twice.
        activeTooltips.delete(tooltipKey);

        // Cancel timeout if it exists
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        const finish = () => {
            // Announced before teardown so whatever replaces the bubble - the
            // bell badge - is queued in the same frame the dot is removed. The
            // other way round leaves one frame showing neither.
            try {
                config?.onHidden?.();
            } catch (error) {
                console.error('Error in bubble onHidden:', error);
            }

            try {
                // destroyTooltip() expects the *target element* - it reads
                // `element.$_popper`, which the object returned by
                // createTooltip() does not have.
                destroyTooltip(element);
            } catch (error) {
                console.error('Error destroying tooltip:', error);
            }

            // Safety net, scoped to this bubble's own popper.
            document.querySelectorAll(`.${tooltipKey}`).forEach((el) => el.remove());

            return true;
        };

        // Scoped to this bubble's popper, so a bubble on another element is
        // never the one that gets animated out.
        const popper = document.querySelector(`.${tooltipKey}`);

        if (immediate || !popper) {
            return Promise.resolve(finish());
        }

        return runOutro(popper, config).then(finish);
    };

    /**
     * Generates the tooltip content as HTML string
     * @param {string|VNode|Function} content - Text string, Vue component/VNode created with h(), or component function () => h()
     * @param {object} config - Configuration
     * @returns {string} - HTML string
     */
    const generateContent = async (content, config) => {
        // Check if content is a function (component function like () => h())
        if (typeof content === 'function') {
            // Call the function to get the VNode/component
            content = content();
        }
        
        // Convert strings to VNodes for consistent processing
        if (typeof content === 'string') {
            content = h('span', content);
        }

        const intro = config.reducedMotion
            ? 'animate-bubble-fade'
            : 'animate-bubble-emerge transform scale-0 opacity-0';

        // Create wrapper VNode and render it to HTML
        const wrapperVNode = h('div', {
            // No overflow-hidden: it would clip the dot on its first pixel.
            class: 'p-2',
            'data-bubble-wrapper': '',
        }, [
            h('div', {
                class: `px-2 py-1 rounded-lg shadow-sm text-white text-xs bg-${config.color}-500 ${intro}`,
                'data-bubble-pill': '',
            }, [
                h('span', {
                    class: 'flex items-center whitespace-nowrap',
                    'data-bubble-label': '',
                }, content)
            ])
        ]);
        
        // Render the VNode to HTML string
        return renderToString(wrapperVNode);
    };

    /**
     * Convenience function for common cases
     * @param {string|HTMLElement} target - Target element
     * @param {string|VNode|Function} text - Text string, Vue component/VNode created with h(), or component function () => h()
     * @param {number} duration - Duration in ms
     */
    const flashBubble = (target, text, duration = 2000) => {
        return showBubble(target, text, { duration });
    };

    /**
     * Hides all active bubbles
     */
    const hideAllBubbles = () => {
        const keys = Array.from(activeTooltips.keys());
        keys.forEach(key => hideBubble(key));
    };

    return {
        showBubble,
        hideBubble,
        hideAllBubbles,
        flashBubble,
    };
};

/**
 * Creates a bubble that can be used to show a bubble on a target element
 * 
 * @returns {Object} - An object with a target element and a bubble function
 * @property {Ref<HTMLElement>} target - The target element
 * @property {Function} flash - The flash function
 */
export const createBubble = (defaultOptions = {}) => {
    const target = ref(null);
    const { showBubble } = useBubble();

    const flash = (text, options = {}) => {
        // One deferred per call: a shared one stays resolved after the first
        // flash, making every later `await flash()` return immediately.
        const { promise, resolve } = deferredPromise();

        // Ensure we have a valid target before trying to show bubble
        const element = unrefElement(target);
        
        if (!element) {
            console.warn('createBubble: target element not found');
            resolve();
            return promise;
        }

        const { onHidden, ...config } = {
            duration: 2000,
            ...defaultOptions,
            ...options,
        };

        showBubble(element, text, {
            ...config,
            onHidden: () => {
                onHidden?.();
                resolve();
            },
        }).then((tooltipKey) => {
            // Nothing was created, so onHidden will never fire.
            if (!tooltipKey) {
                resolve();
            }
        });

        return promise;
    }

    return makeDestructurable(
        { target, flash },
        [target, flash],
    );
};
